import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { SERVICES } from "../data/content";

export default function Consulting() {
  return (
    <>
      <SEO
        title="Consulting & Solutions | ZasyaOnline"
        description="Custom software development, enterprise integration and solution architecture, infrastructure and managed services, contact-centre consulting, AI-powered automation, and technology consulting."
        path="/consulting"
      />
      <section className="pt-28 pb-14 sm:pt-36 sm:pb-16" data-testid="consulting-hero">
        <div className="container-x max-w-3xl">
          <Reveal>
            <span className="eyebrow">Consulting &amp; Solutions</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              The engineering behind our products, applied to your business.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              ZasyaOnline is product-led, but not product-only. The same team designs,
              builds, integrates and runs technology for enterprises — framed around
              your business need, not a service catalogue.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-testid="consulting-services">
        <div className="container-x space-y-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={Math.min(i * 0.05, 0.25)}>
              <article
                className="card-line grid gap-4 p-6 transition-colors duration-200 hover:border-blue-500/50 sm:p-8 lg:grid-cols-12 lg:items-center"
                data-testid={`service-${s.id}`}
              >
                <div className="lg:col-span-1">
                  <span className="font-mono text-sm text-slate-300">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <div className="lg:col-span-4">
                  <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{s.title}</h2>
                </div>
                <p className="text-sm font-medium italic text-teal-800 lg:col-span-3">{s.need}</p>
                <p className="text-sm leading-relaxed text-slate-600 lg:col-span-4">{s.detail}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="consulting-crossref">
        <div className="container-x">
          <Reveal>
            <div className="card-line grid items-center gap-6 p-8 sm:p-10 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  Where consulting meets product
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
                  Some engagements start as consulting and end with a product
                  deployment; some start with a product and need custom integration
                  around it. Our contact-centre consulting and implementation practice,
                  for example, spans consulting, solution architecture, platform
                  implementation, telephony integration and custom development — the
                  same expertise behind myBantu.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:col-span-4 lg:justify-end">
                <Link to="/customers" className="btn-secondary" data-testid="consulting-customers-link">
                  See engagements <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        eyebrow="Consulting enquiries"
        title="Bring us the problem, not the specification."
        body="Tell us what your business is trying to achieve. We'll respond with how we'd approach it — product, consulting, or both."
      />
    </>
  );
}
